import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * User object for audit logging
 */
export interface AuditUser {
    uid: string;
    name?: string;
    email?: string | null;
    role?: string;
}

/**
 * Store object for audit logging
 */
export interface AuditStore {
    id: string;
    name?: string;
    nom?: string;
}

/**
 * Changes object representing before/after state
 */
export interface AuditChanges {
    before?: Record<string, unknown> | unknown[];
    after?: Record<string, unknown> | unknown[];
}

/**
 * Audit log entry structure
 */
export interface AuditLogEntry {
    timestamp: ReturnType<typeof serverTimestamp>;
    action: string;
    entity: string;
    entityId: string;
    userId: string;
    userName: string;
    userRole: string;
    storeId: string | null;
    storeName: string;
    details: Record<string, unknown>;
    changes: AuditChanges | null;
    userAgent: string;
}

/**
 * Logs a critical action to Firestore asynchronously.
 * This function is "fire-and-forget" to avoid blocking the UI.
 *
 * @param action - The action type (e.g., 'CREATE_SALE', 'DELETE_PRODUCT')
 * @param entity - The collection name (e.g., 'sales', 'inventory')
 * @param entityId - The ID of the affected document
 * @param user - The current user object (must contain uid, name/email)
 * @param store - The current store object (optional)
 * @param details - Additional details about the action
 * @param changes - Optional diff { before: {}, after: {} }
 */
export const logAction = async (
    action: string,
    entity: string,
    entityId: string | null | undefined,
    user: AuditUser | null | undefined,
    store: AuditStore | null = null,
    details: Record<string, unknown> = {},
    changes: AuditChanges | null = null
): Promise<void> => {
    try {
        // Validate required fields
        if (!action || !entity) {
            console.warn('Audit Logger: Missing required fields', { action, entity });
            return;
        }

        const safeUser: AuditUser = user || { uid: 'system', email: 'System/Unknown', role: 'system' };

        const logEntry: AuditLogEntry = {
            timestamp: serverTimestamp(),
            action,
            entity,
            entityId: entityId || 'unknown',
            userId: safeUser.uid || 'unknown',
            userName: safeUser.name || safeUser.email || 'Unknown',
            userRole: safeUser.role || 'Unknown',
            storeId: store?.id || null,
            storeName: store?.name || store?.nom || 'Global',
            details: JSON.parse(JSON.stringify(details || {})), // Sanitize undefined values
            changes: changes || null,
            userAgent: navigator.userAgent
        };

        // Fire and forget - we don't await this in the main flow usually,
        // but here we use the promise to catch errors without blocking
        addDoc(collection(db, 'logs'), logEntry).catch(err => {
            console.error('Audit Logger: Failed to write log', err);
        });

    } catch (error) {
        // Failsafe: logging should never break the app
        console.error('Audit Logger: Internal error', error);
    }
};

// Action Constants for consistency
export const AUDIT_ACTIONS = {
    // Sales
    CREATE_SALE: 'CREATE_SALE',
    UPDATE_SALE: 'UPDATE_SALE',
    DELETE_SALE: 'DELETE_SALE',

    // Inventory
    CREATE_PRODUCT: 'CREATE_PRODUCT',
    UPDATE_PRODUCT: 'UPDATE_PRODUCT',
    DELETE_PRODUCT: 'DELETE_PRODUCT',
    ADJUST_STOCK: 'ADJUST_STOCK',
    TRANSFER_INVENTORY: 'TRANSFER_INVENTORY',

    // Customers
    CREATE_CUSTOMER: 'CREATE_CUSTOMER',
    UPDATE_CUSTOMER: 'UPDATE_CUSTOMER',
    DELETE_CUSTOMER: 'DELETE_CUSTOMER',

    // Expenses
    CREATE_EXPENSE: 'CREATE_EXPENSE',
    UPDATE_EXPENSE: 'UPDATE_EXPENSE',
    DELETE_EXPENSE: 'DELETE_EXPENSE',

    // Users & Roles
    CREATE_USER: 'CREATE_USER',
    UPDATE_USER: 'UPDATE_USER',
    DELETE_USER: 'DELETE_USER',
    DEACTIVATE_USER: 'DEACTIVATE_USER',
    REACTIVATE_USER: 'REACTIVATE_USER',
    REVOKE_SESSION: 'REVOKE_SESSION',
    ASSIGN_STORE: 'ASSIGN_STORE',
    UNASSIGN_STORE: 'UNASSIGN_STORE',
    UPDATE_ROLE: 'UPDATE_ROLE',
    CREATE_ROLE: 'CREATE_ROLE',
    DELETE_ROLE: 'DELETE_ROLE',
    UPDATE_PERMISSIONS: 'UPDATE_PERMISSIONS',

    // Auth
    LOGIN: 'LOGIN',
    LOGOUT: 'LOGOUT',

    // Bundles
    CREATE_BUNDLE: 'CREATE_BUNDLE',
    UPDATE_BUNDLE: 'UPDATE_BUNDLE',
    DELETE_BUNDLE: 'DELETE_BUNDLE',

    // Commissions
    MARK_COMMISSION_PAID: 'MARK_COMMISSION_PAID',
    UPDATE_COMPENSATION_CONFIG: 'UPDATE_COMPENSATION_CONFIG',

    // Returns
    CREATE_RETURN: 'CREATE_RETURN',
    UPDATE_RETURN: 'UPDATE_RETURN',
    DELETE_RETURN: 'DELETE_RETURN',

    // Repairs
    CREATE_REPAIR: 'CREATE_REPAIR',
    UPDATE_REPAIR: 'UPDATE_REPAIR',
    DELETE_REPAIR: 'DELETE_REPAIR'
} as const;

export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS];
